import {
  AttachmentAudio,
  BalanceJson,
  Reward,
  UserId,
  Uuid,
} from '../../models/Base';
import { RepositoryElement } from '../../models/Repository';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { GameType } from '../GameStore';

export enum GameRecordType {
  Normal = 'normal',
  Challenge = 'challenge',
  Slideshow = 'slideshow',
  Revision = 'revision',
  Assisted = 'assisted',
  Post = 'post',
}

interface OpponentRecord {
  durationInMs: number;
  score: number; // float
}

interface Result {
  text?: string;
  audios?: (AttachmentAudio | null)[];
  opponentRecord?: OpponentRecord;
}

export enum EvaluationAccuracy {
  Hint = 'hint',
  Low = 'low',
  High = 'high',
  Correct = 'correct',
}

interface TextEvaluationUnitJson {
  unit: string;
  isBlank: boolean;
  score?: number; // float
}

class TextEvaluationUnit implements TextEvaluationUnitJson {
  unit: string;
  isBlank: boolean;
  score: number | undefined; // float

  constructor(json: TextEvaluationUnitJson) {
    this.unit = json.unit;
    this.isBlank = json.isBlank;
    this.score = json.score;
  }

  getAccuracy({ high, low } = { high: 95, low: 50 }): EvaluationAccuracy {
    if (!this.isBlank || this.score === undefined) {
      return EvaluationAccuracy.Hint;
    }
    if (this.score < low) {
      return EvaluationAccuracy.Low;
    }
    if (this.score < high) {
      return EvaluationAccuracy.High;
    }
    return EvaluationAccuracy.Correct;
  }

  setScore(score: number) {
    this.score = score;
  }
}

type TextEvaluationRecordJson = {
  durationInMs?: number;
  units: TextEvaluationUnitJson[];
};

class TextEvaluationRecord implements TextEvaluationRecordJson {
  durationInMs: number | undefined;
  units: TextEvaluationUnit[];

  constructor(json: TextEvaluationRecordJson) {
    this.durationInMs = json.durationInMs;
    this.units = json.units.map((unit) => new TextEvaluationUnit(unit));
  }

  get score(): number | undefined {
    const { total, count } = this.units.reduce(
      (acc, cur) => {
        if (cur.isBlank) {
          acc.total += cur.score ?? 0;
          acc.count += 1;
        }
        return acc;
      },
      { total: 0, count: 0 },
    );
    return count === 0 ? undefined : total / count;
  }
}

export interface TextEvaluationWordJson {
  word: string;
  records?: (TextEvaluationRecordJson | { score?: number })[];
}

export class TextEvaluationWord implements TextEvaluationWordJson {
  word: string;
  records: TextEvaluationRecord[];

  constructor(json: TextEvaluationWordJson) {
    this.word = json.word;
    this.records =
      json.records?.map((record) => {
        if ('units' in record) {
          return new TextEvaluationRecord(record);
        } else {
          return new TextEvaluationRecord({
            units: [{ unit: this.word, isBlank: true, score: record.score }],
          });
        }
      }) ?? [];
  }

  get units(): TextEvaluationUnit[] {
    return this.records[this.records.length - 1]?.units ?? [];
  }

  get canImprove(): boolean {
    return this.units.some((unit) => unit.isBlank);
  }

  get shouldImprove(): boolean {
    return (
      (this.records[this.records.length - 1]?.score ?? 0) < 95 &&
      this.canImprove
    );
  }

  static create(word: string, score: number | undefined): TextEvaluationWord {
    return new TextEvaluationWord({
      word,
      records: [
        { units: [...word].map((unit) => ({ unit, isBlank: true, score })) },
      ],
    });
  }
}
interface InAppEvaluation<W extends TextEvaluationWordJson> {
  gameType: GameType;
  inApp: W[];
  improvement?: W[];
}

// TODO: in app evaluation for conversation and manual evaluation

export interface GameRecordJson<
  T = number,
  W extends TextEvaluationWordJson = TextEvaluationWordJson,
> {
  gameRecordId: Uuid;
  classroomId: Uuid;
  gameSetId: Uuid;
  gameCollectionId: Uuid;
  gameId: Uuid;
  gameHash: string;
  type: GameRecordType;
  slideshowId?: string; // 'auto' / Uuid
  slideshowHash?: string;
  playedBy: UserId;
  evaluatedBy?: UserId;
  createdAt: T;
  sessionStartedAt?: T;
  startedAt: T;
  endedAt?: T;
  evaluatedAt?: T;
  willConfirmAt?: T;
  fee: BalanceJson;
  durationInMs?: number;
  result?: Result;
  reward?: Reward;
  score?: number; // float
  stars?: number; // float
  defeatedOpponents?: number | null;
  evaluation?: InAppEvaluation<W>;
  buddyId?: string; // JoyPetItemId; // null
  bonusReward?: Reward; // include winner reward
  warmUpReward?: Reward;
  teamReward?: Reward;
  continueCount?: number;
  opponentId?: Uuid;
}

export class GameRecord
  extends RepositoryElement
  implements GameRecordJson<Date>
{
  gameRecordId!: Uuid;
  classroomId!: Uuid;
  gameSetId!: Uuid;
  gameCollectionId!: Uuid;
  gameId!: Uuid;
  gameHash!: string;
  type!: GameRecordType;
  slideshowId?: string; // 'auto' / Uuid
  slideshowHash?: string;
  playedBy!: UserId;
  evaluatedBy?: UserId;
  createdAt!: Date;
  sessionStartedAt?: Date;
  startedAt!: Date;
  endedAt?: Date;
  evaluatedAt?: Date;
  willConfirmAt?: Date;
  fee!: BalanceJson;
  durationInMs?: number;
  result?: Result;
  reward?: Reward;
  score?: number; // float
  stars?: number; // float
  defeatedOpponents?: number | null;
  evaluation?: InAppEvaluation<TextEvaluationWord>;
  buddyId?: string; // JoyPetItemId; // null
  bonusReward?: Reward; // include winner reward
  warmUpReward?: Reward;
  teamReward?: Reward;
  continueCount?: number;
  opponentId?: Uuid;

  // local UI data
  isBestInType?: boolean;

  constructor(gameRecord: GameRecordJson<Date | number>) {
    super();
    const { evaluation, ...rest } = gameRecord;
    Object.assign(this, deepTransformTimestamp(rest));
    if (evaluation) {
      this.evaluation = {
        gameType: evaluation.gameType,
        inApp: evaluation.inApp.map((word) => new TextEvaluationWord(word)),
        improvement: evaluation.improvement?.map(
          (word) => new TextEvaluationWord(word),
        ),
      };
    }
  }

  id(): string {
    return this.gameRecordId;
  }

  toJSON(): GameRecordJson {
    return deepTransformDate(this) as unknown as GameRecordJson;
  }

  get completed(): boolean {
    return (
      this.endedAt !== undefined &&
      (this.type === GameRecordType.Slideshow || this.score !== undefined)
    ); //score missing in rated game
  }

  get canPassStation(): boolean {
    if (this.type === GameRecordType.Slideshow) {
      return this.endedAt !== undefined;
    }
    return (
      this.endedAt !== undefined &&
      this.score !== undefined &&
      this.score > 0 &&
      ((this.defeatedOpponents ?? 0) > 0 ||
        this.type === GameRecordType.Assisted)
    );
  }

  get isNormal() {
    return (
      this.type === GameRecordType.Normal ||
      this.type === GameRecordType.Revision
    ); // need to add Challenge type?
  }

  get isAssisted() {
    return this.type === GameRecordType.Assisted;
  }

  get isSlideshow() {
    return this.type === GameRecordType.Slideshow;
  }

  get evaluatedEndedAt(): Date | undefined {
    return this.evaluatedAt !== undefined
      ? this.willConfirmAt ?? this.evaluatedAt
      : undefined;
  }

  isBetterThan(
    other?: Pick<GameRecordJson<Date | number>, 'score' | 'durationInMs'>,
  ) {
    if (other === undefined) {
      return false;
    }

    if (other.score === undefined) {
      return this.score !== undefined;
    }

    if (this.score !== other.score) {
      return (this.score ?? -Infinity) > other.score;
    }

    return (this.durationInMs ?? Infinity) < (other.durationInMs ?? Infinity);
  }

  match(record: GameRecord): boolean {
    return (
      this.classroomId === record.classroomId &&
      this.gameSetId === record.gameSetId &&
      this.type === record.type &&
      this.gameId === record.gameId &&
      this.slideshowId === record.slideshowId &&
      this.playedBy === record.playedBy &&
      this.startedAt?.getTime() === record.startedAt?.getTime()
    );
  }
}
